import { useEffect, useState, useCallback } from "react";
import { UserPlus } from "lucide-react";
import { getRoles } from "../api/roleApi";                  // owner API
import { getRolesForPortal } from "../api/staffPortalApi";  // portal API
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Input,
  Select,
  Button,
} from "./ui";

const GENDER_OPTIONS = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "Other", label: "Other" },
];

const StaffForm = ({ onSubmit, loading, isPortal = false }) => {
  const [roles, setRoles] = useState([]);

  const [formData, setFormData] = useState({
    roleId: "",
    name: "",
    gender: "Male",
    email: "",
    mobile: "",
    address: "",
  });

  const loadRoles = useCallback(async () => {
    try {
      // ✅ Call correct API based on who is logged in
      const data = isPortal
        ? await getRolesForPortal()  // uses staffToken cookie → /staff-portal/roles
        : await getRoles();          // uses token cookie     → /roles/all

      setRoles((data || []).filter((r) => r.category === "STAFF"));
    } catch (error) {
      console.error(error);
    }
  }, [isPortal]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const submitHandler = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const roleSelectOptions = [
    { value: "", label: "Select Role" },
    ...roles.map((role) => ({ value: role._id, label: role.roleName })),
  ];

  return (
    <Card>
      <CardHeader className="border-b bg-neutral-50/50 flex flex-row items-center justify-between gap-4">
        <CardTitle className="flex items-center gap-2">
          <UserPlus className="h-5 w-5 text-primary-600 dark:text-primary-400 shrink-0" />
          <span>Add Staff Member</span>
        </CardTitle>

        <Button
          type="submit"
          form="staff-form"
          variant="primary"
          isLoading={loading}
          className="px-6"
        >
          {loading ? "Creating..." : "Create Staff"}
        </Button>
      </CardHeader>

      <CardContent className="p-4 sm:p-6">
        <form id="staff-form" onSubmit={submitHandler} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Select
              label="Staff Role"
              name="roleId"
              value={formData.roleId}
              onChange={(val) => setFormData((prev) => ({ ...prev, roleId: val }))}
              options={roleSelectOptions}
              required
            />

            <Input
              label="Full Name"
              type="text"
              name="name"
              placeholder="Enter full name"
              value={formData.name}
              onChange={handleChange}
              required
            />

            <Select
              label="Gender"
              name="gender"
              value={formData.gender}
              onChange={(val) => setFormData((prev) => ({ ...prev, gender: val }))}
              options={GENDER_OPTIONS}
            />

            <Input
              label="Email Address"
              type="email"
              name="email"
              placeholder="e.g. staff@school.com"
              value={formData.email}
              onChange={handleChange}
              required
            />

            <div className="sm:col-span-2">
              <Input
                label="Mobile Number"
                type="text"
                name="mobile"
                placeholder="10-digit mobile number"
                value={formData.mobile}
                onChange={handleChange}
                required
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-neutral-700 dark:text-neutral-200 mb-1.5">
                Residential Address
              </label>
              <textarea
                name="address"
                placeholder="Enter complete address..."
                value={formData.address}
                onChange={handleChange}
                rows={3}
                className="w-full rounded-card border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-3 text-xs text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default StaffForm;